import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import {
  createFleetVehicleServiceAPI,
  getFleetVehicleServiceAPI,
  updateFleetVehicleServiceAPI,
} from "services/api/fleet/vehicles/services/api";
import { initialFleetVehicleServiceEditForm } from "services/api/fleet/vehicles/services/consts";
import { transformFleetVehicleServiceEditFormValues } from "services/api/fleet/vehicles/services/transformations";
import { FleetVehicleServiceEditFormValues } from "services/api/fleet/vehicles/services/types";
import { VehicleLocationFormValues } from "services/api/fleet/vehicles/locations/types";
import { VehicleLocationTypeEnum } from "services/api/fleet/vehicles/locations/types";
import { initialVehicleLocationForm } from "services/api/fleet/vehicles/locations/consts";
import { Errors } from "services/forms/validation/types";
import { clearChangedErrors, validateYup } from "services/forms/validation/validation";

// UI
import { Loader } from "ui/components/Loader";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import WizardStep from "ui/components/Wizard/WizardStep";
import invoiceAnimation from "ui/theme/animations/invoice.json";

import ServiceBasicInfoForm from "./components/ServiceBasicInfoForm/ServiceBasicInfoForm";
import ServiceDocumentForm from "./components/ServiceDocumentForm/ServiceDocumentForm";
import { VehicleServiceEditCardProps } from "./types";
import { FLEET_VEHICLE_SERVICE_EDIT_FORM_SCHEMA } from "./validation";

/**
 * Čarobnjak servisnog naloga. Otvara ga promjena statusa u "Nedostupno – Servis" i tab
 * "Servisi" na kartici vozila — jedno mjesto unosa, da se isti podatak ne upisuje dvaput.
 *
 * Kad ga otvori promjena statusa, ne sprema sam: unos vraća pozivatelju (`onSubmit`), koji
 * nalog i status šalje jednim zahtjevom.
 */
const VehicleServiceEditCard: React.FC<VehicleServiceEditCardProps> = (props) => {
  const {
    show,
    vehicleId,
    serviceId,
    onSubmit,
    initialLocation,
    initialValues,
    isSaving,
    onSuccess,
    onClose,
  } = props;

  const { t } = useTranslate();

  const [stepCurrent, setStepCurrent] = useState<number>(0);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FleetVehicleServiceEditFormValues>(
    initialFleetVehicleServiceEditForm
  );
  const [location, setLocation] = useState<VehicleLocationFormValues | null>(null);
  const [locationTouched, setLocationTouched] = useState<boolean>(false);

  useEffect(() => {
    if (!show) return;

    setStepCurrent(0);
    setErrors({});
    setLocationTouched(false);
    setLocation(initialLocation ?? null);

    if (serviceId) {
      fetchService(serviceId);
      return;
    }

    // Nalog se otvara u trenutku kad vozilo odlazi na servis — datum je današnji dan,
    // osim kad ga pozivatelj već zna (premještaj nosi svoj datum i servisera).
    setFormValues({
      ...initialFleetVehicleServiceEditForm,
      receivedDate: new Date(),
      ...initialValues,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, serviceId, vehicleId]);

  // Odabir servisa predlaže i lokaciju vozila, dok je korisnik sam ne promijeni.
  useEffect(() => {
    if (!show || locationTouched || !location) return;
    if (!formValues.contactId) return;

    setLocation((prev) =>
      prev
        ? {
            ...prev,
            locationType: VehicleLocationTypeEnum.SERVICE,
            contactId: formValues.contactId,
          }
        : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, locationTouched, formValues.contactId]);

  const fetchService = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetVehicleServiceAPI(id);
      setFormValues(transformFleetVehicleServiceEditFormValues(result));
    } catch {
      setFormValues(initialFleetVehicleServiceEditForm);
    }
    setIsLoading(false);
  };

  const handleFieldsChange = useCallback(
    (values: FleetVehicleServiceEditFormValues) => {
      clearChangedErrors(formValues, values, setErrors);
      setFormValues(values);
    },
    [formValues]
  );

  const handleLocationChange = useCallback(
    (values: VehicleLocationFormValues) => {
      clearChangedErrors(location ?? {}, values, setErrors);
      setLocationTouched(true);
      setLocation(values);
    },
    [location]
  );

  const handleDocumentsChange = useCallback((uploads: FleetVehicleServiceEditFormValues["documentUploads"]) => {
    setFormValues((prev) => ({ ...prev, documentUploads: uploads }));
  }, []);

  const handleNextStep = useCallback(() => {
    if (stepCurrent === 0) {
      const isValid = validateYup(
        formValues,
        FLEET_VEHICLE_SERVICE_EDIT_FORM_SCHEMA,
        setErrors
      );
      if (!isValid) return;
    }

    setStepCurrent((step) => step + 1);
  }, [stepCurrent, formValues]);

  const handleBackStep = useCallback(() => {
    setStepCurrent((step) => step - 1);
  }, []);

  const handleFinish = useCallback(async () => {
    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_SERVICE_EDIT_FORM_SCHEMA,
      setErrors
    );
    if (!isValid) {
      setStepCurrent(0);
      return;
    }

    // Promjena statusa sprema nalog zajedno sa statusom — čarobnjak samo preda unos.
    if (onSubmit) {
      onSubmit(formValues, location);
      return;
    }

    setIsLoading(true);
    try {
      if (serviceId) {
        await updateFleetVehicleServiceAPI(
          serviceId,
          formValues,
          formValues.documentUploads,
          t
        );
      } else {
        await createFleetVehicleServiceAPI(
          vehicleId,
          formValues,
          formValues.documentUploads,
          t
        );
      }

      onSuccess?.(formValues, location);
      onClose();
    } catch (err) {
      // Greška ide kroz API interceptor; modal ostaje otvoren da se unos ne izgubi.
      console.error("Greška prilikom spremanja servisnog naloga:", err);
    } finally {
      setIsLoading(false);
    }
  }, [formValues, location, onSubmit, serviceId, vehicleId, t, onSuccess, onClose]);

  const steps = useMemo(
    () => [
      <WizardStep key="info" stepNumber={0} stepCurrent={stepCurrent}>
        <ServiceBasicInfoForm
          values={formValues}
          errors={errors}
          onChange={handleFieldsChange}
          location={location}
          onLocationChange={handleLocationChange}
        />
      </WizardStep>,
      <WizardStep key="documents" stepNumber={1} stepCurrent={stepCurrent}>
        <ServiceDocumentForm
          uploads={formValues.documentUploads}
          onChange={handleDocumentsChange}
        />
      </WizardStep>,
    ],
    [
      stepCurrent,
      formValues,
      errors,
      location,
      handleFieldsChange,
      handleLocationChange,
      handleDocumentsChange,
    ]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      position="side"
      size="full"
      intro={{
        content: (
          <ModalIntro
            animation={invoiceAnimation}
            title="Vozilo ide na servis!"
            description="Zabilježite kod koga je vozilo, što se radi i koliko je trajalo. Troškovi i štete vežu se na isti nalog, pa se poslije zna koliko je popravak koštao."
            show={show}
          />
        ),
      }}
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={serviceId ? "Ažuriranje servisnog naloga" : "Novi servisni nalog"}
        onHide={onClose}
      />
      {/* Podnožje je pravi WizardFooter, pa donji razmak za "fixed" podnožje otpada. */}
      <ModalBody fixedFooterSpacing={false}>
        <Wizard steps={steps} />
        <Loader isLoading={isLoading}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading || !!isSaving}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(VehicleServiceEditCard);
